export const NODEJS_COMMIT_HASH = process.env.COMMIT_HASH;
export const NODEJS_COMMIT_TIMESTAMP = process.env.COMMIT_TIMESTAMP;

export const COMMIT_TIMESTAMP_ATTRIBUTE_NAME = 'commit-timestamp';
export const COMMIT_HASH_ATTRIBUTE_NAME = 'commit-hash';
/**
 * Clients with a commit version before this timestamp will be classified as stale and will be shown a
 * Warning or error.
 *
 * Update this constant with your commits timestamp if you want users to refresh and have your commit in their bundle
 * You can find your commit timestamp with `git show -s --format=%ct <commit>`
 *
 * @see build-timestamp-tag.ts for how each bundle's timestamp is built
 */
/**
 * git show -s --format=%ct 0ce020c53f3acff9afb6830035508ee9b663cea1
 * https://bitbucket.org/atlassian/canvas/commits/0ce020c53f3acff9afb6830035508ee9b663cea1
 *
 * Update programtically via `yarn bumpcommit` from the root of the repository
 */
export const OLDEST_SUPPORTED_COMMIT_TIMESTAMP = 1770161776;

export interface ManifestVersionData {
  oldestSupportedCommitTimestamp: number;
  commit: string;
}

/**
 * @returns A unix epoch timestamp in seconds that represents the time at which
 * this bundle's commit was made. Uses git's committer date metadata.
 *
 * Will be undefined if no build timestamp was found.
 *
 * Will return Number.NaN if a timestamp was found but not parseable.
 *
 * @param domDocument The document to search for the timestamp. Defaults to the
 * global document. Mainly useful for testing purposes.
 *
 * @remarks
 * Use this value instead of the commit hash to refer to build
 * versions as it's numerically sortable which can be useful for "Any version
 * after XYZ time" type queries.
 *
 * E.g.
 * - Turning on feature flags after a certain date
 *
 * @remarks
 * This value will be constant between redeployments and rebuilds of the exact same
 * bundle.
 *
 * @privateRemarks
 * The choice to mount the metadata on the html tag was mostly arbitrary. Mostly,
 * because it's unique and easy to query for.
 *
 * @privateRemarks
 * Although milliseconds could technically be included in this timestamp,
 * using seconds avoids confusion in the event that build nodes have slightly
 * different times due to times being out of sync (very easy to happen when
 * depending on the time at which they last called their NTP server)
 *
 * @see https://product-fabric.atlassian.net/browse/CNS-4293 which contains some
 * extra context for decision making and other considered alternatives.
 *
 * @see https://bitbucket.org/atlassian/canvas/pull-requests/5690 which also
 * contains some extra context.
 *
 * @see https://learn.microsoft.com/en-us/azure/devops/repos/git/git-dates?view=azure-devops
 * which explains dates simply
 */
export const getCommitTimestamp = (
  domDocument = globalThis.document
): number | undefined => {
  const timestamp = domDocument
    .querySelector('html')
    ?.getAttribute(COMMIT_TIMESTAMP_ATTRIBUTE_NAME);

  if (timestamp === null || timestamp === undefined) {
    return undefined;
  }

  return Number.parseInt(timestamp);
};

/**
 * Value computed once - only changes on reload.
 * @description Avoids requerying of the DOM once loaded once
 */
let commitHash: string | undefined;

/**
 * @returns The commit hash from which the frontend bundle was created from
 *
 * @param domDocument The document to search for the hash. Defaults to the
 * global document. Mainly useful for testing purposes.
 *
 * @remarks
 * For versioning, try to use {@link getCommitTimestamp} as it's not possible to
 * chronologically sort commit hashes.
 *
 * @privateRemarks
 * The choice to mount the metadata on the html tag was mostly arbitrary. Mostly,
 * because it's unique and easy to query for.
 */
export const getCommitHash = (domDocument = globalThis.document): string | undefined => {
  if (!commitHash) {
    commitHash =
      domDocument.querySelector('html')?.getAttribute(COMMIT_HASH_ATTRIBUTE_NAME) ??
      undefined;
  }

  return commitHash;
};

/**
 * Use only for testing purposes.
 */
export const resetCommitHash = () => {
  commitHash = undefined;
};
